/**
 * Seeded persona generation (shared contract, see types.ts). Same seed → same persona.
 */
import type { ClassId } from '@keystone/sim';
import { createSeededRng } from '@keystone/sim';
import type { Archetype, Persona, PersonaTraits } from './types';

export const ARCHETYPES: readonly Archetype[] = ['tryhard', 'chill', 'newbie', 'goblin', 'distracted', 'clown', 'silent', 'leader'];

type Range = readonly [number, number];
/** Trait ranges per archetype; unlisted traits use DEFAULT. */
const DEFAULT: Record<keyof PersonaTraits, Range> = {
  skill: [0.35, 0.7],
  temper: [0.2, 0.5],
  patience: [0.4, 0.8],
  afk: [0.05, 0.25],
  greed: [0.05, 0.3],
  chatty: [0.3, 0.6],
  toxicity: [0.1, 0.4],
  tolerance: [0.4, 0.8],
};
const RANGES: Record<Archetype, Partial<Record<keyof PersonaTraits, Range>>> = {
  tryhard: { skill: [0.8, 1], temper: [0.7, 1], patience: [0.05, 0.3], afk: [0, 0.05], toxicity: [0.5, 0.9], tolerance: [0.1, 0.35], chatty: [0.3, 0.6] },
  chill: { skill: [0.45, 0.7], temper: [0, 0.2], patience: [0.8, 1], toxicity: [0, 0.15], tolerance: [0.7, 1] },
  newbie: { skill: [0.05, 0.35], temper: [0, 0.2], patience: [0.6, 0.9], toxicity: [0, 0.1], tolerance: [0.8, 1], chatty: [0.4, 0.8] },
  goblin: { greed: [0.7, 1], skill: [0.4, 0.7], afk: [0.05, 0.2] },
  distracted: { afk: [0.6, 1], skill: [0.3, 0.65], chatty: [0.3, 0.7] },
  clown: { chatty: [0.75, 1], skill: [0.3, 0.65], toxicity: [0.2, 0.5] },
  silent: { chatty: [0, 0.12], skill: [0.55, 0.85], temper: [0.1, 0.4] },
  leader: { skill: [0.65, 0.9], chatty: [0.6, 0.9], patience: [0.6, 0.9], toxicity: [0, 0.25], tolerance: [0.5, 0.8] },
};

const NAME_A = ['Grim', 'Moss', 'Ash', 'Brew', 'Thorn', 'Kel', 'Vex', 'Dusk', 'Bran', 'Lumi', 'Sap', 'Rok', 'Fen', 'Ivo', 'Zul', 'Pip', 'Mor', 'Quill', 'Tav', 'Nyx'];
const NAME_B = ['jaw', 'beard', 'whisper', 'tusk', 'wick', 'mane', 'shade', 'fang', 'toe', 'bloom', 'grin', 'hammer', 'loaf', 'brook', 'spark', 'stomp'];
const HANDLES = ['xXHealzXx', 'Pumperino', 'Dotsalot', 'Tankyboi', 'Kickmeplz', 'Lootgoblin', 'Afkbrb', 'Frontalfan', 'Notmyfault', 'Pullerino', 'Stoodinfire', 'Wipelord'];

const BIOS: Record<Archetype, readonly string[]> = {
  tryhard: ['Will not wait for you to drink.', 'Has a spreadsheet for this.', 'Parses purple, tolerates nothing.'],
  chill: ['Here for the vibes.', 'Never once complained. Unsettling.', 'Wipes are just practice.'],
  newbie: ['Still learning which way the frontal goes.', 'Says sorry a lot.', 'Read a guide once.'],
  goblin: ['Has never walked past a node.', 'The chest is RIGHT THERE.', 'Mains gathering, plays dungeons on the side.'],
  distracted: ['Someone is always at the door.', 'Plays with a cat on the keyboard.', '"one sec" is a lifestyle.'],
  clown: ['Emotes during boss pulls.', 'Lives for the bit.', 'Has named every mob "Steve".'],
  silent: ['Says nothing. Does everything.', 'Communicates in ready checks.', 'Probably a professional.'],
  leader: ['Calls every pull.', 'Has a plan. Has a backup plan.', 'Keeps the group together.'],
};

export interface GenerateOpts {
  classId: ClassId;
  seed: number;
  archetype?: Archetype;
  /** Names already taken in the roster (generation retries to stay unique). */
  taken?: ReadonlySet<string>;
}

export function generatePersona(opts: GenerateOpts): Persona {
  const rng = createSeededRng(opts.seed >>> 0);
  const archetype = opts.archetype ?? (rng.pick(ARCHETYPES) as Archetype);
  const ranges = { ...DEFAULT, ...RANGES[archetype] };
  const traits = {} as PersonaTraits;
  for (const k of Object.keys(DEFAULT) as (keyof PersonaTraits)[]) {
    const [lo, hi] = ranges[k];
    traits[k] = Math.round(rng.range(lo, hi) * 100) / 100;
  }
  let name = '';
  for (let i = 0; i < 20 && (!name || opts.taken?.has(name)); i++) {
    name = rng.chance(0.25) ? (rng.pick(HANDLES) as string) : `${rng.pick(NAME_A)}${rng.pick(NAME_B)}`;
    if (opts.taken?.has(name)) name += rng.int(2, 99);
  }
  return {
    id: `p${(opts.seed >>> 0).toString(36)}`,
    name,
    classId: opts.classId,
    archetype,
    traits,
    grudges: [],
    bio: rng.pick(BIOS[archetype]) as string,
    seed: rng.int(1, 0x7fffffff),
  };
}

/** Bot skill preset a persona plays at (skill trait → preset). */
export function personaPreset(p: Pick<Persona, 'traits'>): 'novice' | 'competent' | 'expert' {
  return p.traits.skill < 0.4 ? 'novice' : p.traits.skill < 0.75 ? 'competent' : 'expert';
}

/**
 * Whether `a` refuses to queue with `b` (grudge, or b's temper beyond a's tolerance). High
 * tolerance = puts up with more: a newbie (tolerance 0.8+) never refuses on temper, a tryhard
 * (0.1–0.35) won't queue with another hothead.
 */
export function refusesToGroup(a: Persona, b: Persona): boolean {
  return a.grudges.includes(b.id) || b.traits.temper > a.traits.tolerance + 0.35;
}
